<script setup lang="ts">
/**
 * UI-kit component
 * Category: Tutors
 * Name: TutorAvatar
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=3402-86
 *
 * The masked tutor photo alone — no plate, no radius, no surface of its own: the
 * cut-out sits on whatever background the host shows (the division of duties with
 * TutorPhoto, which owns the box, the crop and the placeholder). Fills its container.
 * Carries the fixed 33-tutor roster as bundled assets, addressed by the `name` axis
 * mirroring the Figma set (kebab keys; variant values are roster keys, NOT the display
 * name — GRWT-510 note 5). The roster is final: adding a tutor means adding the image
 * and the union member here and releasing, not a CMS change (product decision by
 * Andrii on GRWT-510, inverting the task's note 4 — Strapi stores the roster key).
 * NOT the same component as the DS package's TutorAvatar (a fixed 42x42 identity
 * marker) — import from @UI/Tutors/TutorAvatar, not from the package.
 */
// types
import type { TutorAvatarProps } from '../types/interfaces'
// hooks
import { useTutorAvatar } from '../composables/use-tutor-avatar.hook'

defineOptions({
  name: 'TutorAvatar',
})

const props = withDefaults(defineProps<TutorAvatarProps>(), {
  alt: '',
})

const {
  refSrc,
  refWebpSrc,
} = useTutorAvatar({ props })
</script>

<template>
  <picture>
    <source
      type="image/webp"
      :srcset="refWebpSrc"
    >
    <img
      class="tutor-avatar"
      :src="refSrc"
      :alt="alt"
      width="264"
      height="264"
      loading="lazy"
      decoding="async"
    >
  </picture>
</template>

<style lang="scss" scoped src="../styles/_tutor-avatar.scss" />
